/**
 * Run-scoped logging utility for deterministic debugging
 * Provides collision-resistant run IDs and structured trace logging
 */

export type Phase =
  | 'bootstrap'
  | 'catalog-hydrating'
  | 'catalog-ready'
  | 'mc-fast'
  | 'mc-refine'
  | 'throughput'
  | 'memory'
  | 'ui-commit'
  | 'watchdog'
  | 'error'
  | 'idle';

export interface Trace {
  t: number;
  runId: string;
  catalogHash?: string;
  scenarioHash?: string;
  source: string;
  phase: Phase;
  msg: string;
  extra?: Record<string, unknown>;
}

/**
 * Generate collision-resistant run ID using timestamp and random string
 * Format: {timestamp-base36}-{random-6chars}
 */
export const newRunId = (): string => {
  const timestamp = Date.now().toString(36);
  const random = Math.random().toString(36).slice(2, 8);
  return `${timestamp}-${random}`;
};

/**
 * Log structured trace with [trace] prefix for filtering
 * All traces are JSON format for machine readability
 */
export const log = (trace: Trace): void => {
  console.log(`[trace] ${JSON.stringify(trace)}`);
};

/**
 * Helper to create trace with current timestamp
 */
export const createTrace = (
  runId: string,
  source: string,
  phase: Phase,
  msg: string,
  extra?: Record<string, unknown>,
): Trace => ({
  t: Date.now(),
  runId,
  source,
  phase,
  msg,
  extra,
});

/**
 * Alias for log - preferred in user-facing code
 */
export const trace = log;
